import {ApolloQueryResult} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {useState} from 'react';

import {showAnalysisProgressTrayTask} from 'analysis-tool/progress/AnalysisProgressTrayTask';
import BenchlingQuery, {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import * as AutomationPipelinesDAO from 'lab-automation/automation-pipelines/AutomationPipelinesDAO';
import {
  GetAnalysesForAssayRun,
  GetAnalysesForAssayRunVariables,
} from 'results/analysis-integration/__generated/GetAnalysesForAssayRun';
import {
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode_configVariables_AnalysisTemplateVariableNode as AnalysisTemplateVariableNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {GetAssayRunAnalysisIds} from 'results/analysis-integration/__generated/GetAssayRunAnalysisIds';
import {
  GET_ANALYSES_FOR_ASSAY_RUN_QUERY,
  isVariableMulti,
} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {AssayGroupProps} from 'runs/assay-group/AssayGroup';
import {OverflowDropdown} from 'runs/assay-group/AssayGroupEditButtons';
import AssayRunFields from 'runs/assay-group/AssayRunFields';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import AnalysisChip from 'ui-library/chips/AnalysisChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';
import * as TaskRunner from 'util/TaskRunner';

import {GetFileNameQuery} from './__generated/GetFileNameQuery';
import SelectPipelineFilesModal from './SelectPipelineFilesModal';

export const GET_FILE_NAME_QUERY = gql`
  query GetFileNameQuery($fileIds: [StrictString!]!) @skipAudit @engComponent(name: PIPELINES) {
    files(ids: $fileIds) {
      ... on FileNode {
        __typename
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface FileVariableBinding {
  variableId: string;
  variableValue: string | Array<string> | null;
}

/** @package */
export interface ExpandedState {
  inputIsExpanded: boolean | null;
  outputIsExpanded: boolean | null;
}
/**
 * @package
 * State of input/output sections in the run part.
 *
 * Input defaults to expanded and output defaults to null (i.e. collapsed).
 */
export const integrationExpandedGlobalClientState = makeUseGlobalClientState<ExpandedState>({
  inputIsExpanded: true,
  outputIsExpanded: null,
});

export const fileVariableBindingsGlobalClientState =
  makeUseGlobalClientState<Array<FileVariableBinding> | null>(null);

/** @package */
export function isVariableRequired(configVariable: AnalysisTemplateVariableNode): boolean {
  // Variable is considered required if it fulfills both conditions:
  // 1. Explicitly marked required
  // 2. If it's an isMulti variable, then minItems is defined and minItems > 0
  // The variable is not considered required variable.required === true but minItems === 0, because the lack
  // of a minimum implies that the field doesn't actually need to be populated.
  return (
    configVariable.variableTypeConfigSpec.required &&
    (configVariable.variableTypeConfigSpec.kind !== 'array' ||
      (configVariable.variableTypeConfigSpec.minItems != null &&
        configVariable.variableTypeConfigSpec.minItems > 0))
  );
}

export interface RunAnalysisAutomationParams {
  runApiId: string;
  fileVariableBindings: Array<FileVariableBinding>;
  previousAnalysisIds: Array<string>;
  onSaveRunFields: () => Promise<void>;
  refetchAnalysisIds: () => Promise<ApolloQueryResult<GetAssayRunAnalysisIds>>;
}

export async function runAnalysisAutomation(params: RunAnalysisAutomationParams): Promise<void> {
  const {runApiId, fileVariableBindings, previousAnalysisIds, onSaveRunFields, refetchAnalysisIds} = params;

  // Save the run schema fields first
  await onSaveRunFields();

  // Then run the automation template
  const taskInfo = await AutomationPipelinesDAO.createAnalysisFromRun(runApiId, fileVariableBindings);
  await TaskRunner.resumeTaskPromise(taskInfo.status_url);

  // Refetch the IDs of analyses created from the run
  const refetchResult = await refetchAnalysisIds();

  // Find and show progress for any new analyses
  let newAnalysisIds: Array<string> = [];
  if (refetchResult.data?.run && !isInaccessible(refetchResult.data.run)) {
    newAnalysisIds = refetchResult.data.run.analysisIds || [];

    const addedAnalysisIds = newAnalysisIds.filter((id: string) => !previousAnalysisIds.includes(id));
    addedAnalysisIds.forEach((analysisId: string) => {
      showAnalysisProgressTrayTask(analysisId);
    });
  }
}

interface AnalysisTemplateIntegrationRunToolbarProps {
  // OverflowDropdown props
  assayRun: AssayRun;
  assayRunSchema: AssayRunSchema;
  handleRemoveAssayRun: () => void;
  locked: boolean;
  // Analysis template integration-specific props
  canRunAutomation: boolean;
  onRunAutomation: () => void;
  isRunning: boolean;
  canSave: boolean;
  onSave: () => void;
  isSaving: boolean;
}

export function AnalysisTemplateIntegrationRunToolbar({
  assayRun,
  assayRunSchema,
  handleRemoveAssayRun,
  locked,
  canRunAutomation,
  onRunAutomation,
  isRunning,
  canSave,
  onSave,
  isSaving,
}: AnalysisTemplateIntegrationRunToolbarProps): JSX.Element {
  return (
    <div className={css(styles.toolbar)}>
      {/**
       * When the automation is run, both isSaving and isRunning may be true (since we save the fields before
       * running the automation. In this case, we prefer displaying "Running" over "Saving".
      ) */}
      {isRunning ? (
        <SaveStatusIndicator saveStatus={SaveState.SAVING} messageOverride="Running..." />
      ) : (
        isSaving && <SaveStatusIndicator saveStatus={SaveState.SAVING} />
      )}
      <Tooltip title="Fill out all required fields in order to run automation" disabled={canRunAutomation}>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Run automation"
          onClick={onRunAutomation}
          disabled={!canRunAutomation}
        />
      </Tooltip>
      <Tooltip title={!canSave ? 'Run fields are already saved' : undefined} disabled={canSave}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          size={ButtonSize.SMALL}
          text="Save without running"
          onClick={onSave}
          disabled={!canSave}
        />
      </Tooltip>
      <OverflowDropdown
        allowArchive={!locked}
        allowCopyId={!locked}
        assayRun={assayRun}
        assayRunSchema={assayRunSchema}
        handleRemoveAssayRun={handleRemoveAssayRun}
        isAutomationRun={false}
      />
    </div>
  );
}

interface InputFileFieldProps {
  configVariable: AnalysisTemplateVariableNode;
  binding: FileVariableBinding;
  setBinding: (newVariableValue: string | Array<string> | null) => void;
  runId: string;
}

function InputFileField({configVariable, binding, setBinding, runId}: InputFileFieldProps): JSX.Element {
  const [isUploading, setIsUploading] = useState(false);

  const variableName =
    configVariable.name.length > 0 ? configVariable.name : `File input for ${configVariable.id}`;
  const isMulti = isVariableMulti(configVariable.variableTypeConfigSpec);

  const minItems = isMulti ? configVariable.variableTypeConfigSpec.minItems : null;
  const maxItems = isMulti ? configVariable.variableTypeConfigSpec.maxItems : null;
  let limitInformation: string | null = null;
  if (minItems != null && maxItems != null) {
    limitInformation = `Accepts ${minItems} to ${maxItems} files`;
  } else if (minItems != null) {
    limitInformation = `Accepts ${minItems} or more files`;
  } else if (maxItems != null) {
    limitInformation = `Accepts ${maxItems} or fewer files`;
  }

  const result = useSkippableBenchlingQuery<GetFileNameQuery>(GET_FILE_NAME_QUERY, {
    variables: {fileIds: isMulti ? binding.variableValue : [binding.variableValue]},
    skip: binding.variableValue == null || binding.variableValue.length === 0,
  });
  const selectedFilesText = isUploading
    ? 'Uploading...'
    : binding.variableValue == null || binding.variableValue.length === 0
      ? 'No file selected'
      : result.type === QueryResultType.SUCCESS && result.data.files.every((file) => !isInaccessible(file))
        ? result.data.files.map((file) => file.name).join(', ')
        : '';

  // Handles type translation
  function onSelectFiles(ids: Array<string>): void {
    if (ids.length === 0) {
      setBinding(null);
    } else if (!isMulti) {
      setBinding(ids[0]);
    } else {
      setBinding(ids);
    }
  }

  const onUploadFiles = (
    files: Array<File>,
    // IDs of files that have already been selected. If isMulti=false, or the desired behavior is that
    // selectedFileIds is overwritten, then the caller can just pass in an empty list.
    selectedFileIds: Array<string>
  ): JQueryPromise<void> => {
    setIsUploading(true);
    return AutomationPipelinesDAO.createFilesFromUploads(runId, files, {
      onFilesUploaded: async (s3Locations: Array<string>) => {
        const response = await AutomationPipelinesDAO.bulkCreatePipelineFiles(runId, s3Locations);
        onSelectFiles([...selectedFileIds, ...response.fileIds]);
        setIsUploading(false);
      },
    });
  };

  return (
    <div className={css(styles.fileFieldWrapper)}>
      <div>
        <FieldLabel label={variableName} isRequired={isVariableRequired(configVariable)} />
        {limitInformation != null && (
          <HelpButton
            renderContent={() => limitInformation}
            iconClassName="u-mini-spacing-left"
            placement={FloatingPlacement.RIGHT}
            interaction={FloatingInteraction.HOVER}
          />
        )}
      </div>
      <div className={css(styles.fileSelectorWrapper)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          fullWidth={false}
          text={`Select file${isMulti ? 's' : ''}`}
          icon={isUploading ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : undefined}
          onClick={() => {
            runModal(SelectPipelineFilesModal, {
              title: variableName,
              fileIds:
                binding.variableValue == null
                  ? []
                  : typeof binding.variableValue === 'string'
                    ? [binding.variableValue]
                    : binding.variableValue,
              isMulti,
              minItems: minItems ?? undefined,
              maxItems: maxItems ?? undefined,
              onSelectFiles,
              onUploadFiles,
            });
          }}
        />
        <div className={css(styles.selectedFileText)}>{selectedFilesText}</div>
      </div>
    </div>
  );
}

interface InputFileFieldsProps {
  analysisTemplate: AnalysisTemplateNode;
  runId: string;
  fileVariableBindings: Array<FileVariableBinding>;
  setFileVariableBindings: (newVariableBindings: Array<FileVariableBinding> | null) => void;
}

function InputFileFields({
  analysisTemplate,
  runId,
  fileVariableBindings,
  setFileVariableBindings,
}: InputFileFieldsProps): JSX.Element {
  // TODO: Use fileVariableBindings to populate file name next to button, and call setFileVariableBindings to
  // update correct variable when file is selected
  return (
    <>
      {fileVariableBindings.map((binding, i) => (
        <InputFileField
          configVariable={analysisTemplate.configVariables.find(({id}) => id === binding.variableId)!}
          binding={binding}
          setBinding={(newVariableValue: string | Array<string> | null) => {
            setFileVariableBindings(
              fileVariableBindings.map((otherBinding) =>
                binding.variableId === otherBinding.variableId
                  ? {...otherBinding, variableValue: newVariableValue}
                  : otherBinding
              )
            );
          }}
          runId={runId}
          key={binding.variableId}
        />
      ))}
    </>
  );
}

/** @package */
export type AnalysisTemplateIntegrationRunAssayGroupBodyProps = {
  // Props that are used by AssayRunFields
  visibleRunFields: Array<SchemaField>;
  // Additional analysis template integration run props
  analysisTemplate: AnalysisTemplateNode;
  expandedState: ExpandedState;
  setExpandedState: (newExpandedState: ExpandedState) => void;
  fileVariableBindings: Array<FileVariableBinding> | null;
  setFileVariableBindings: (newVariableBindings: Array<FileVariableBinding> | null) => void;
  analysisIds: Array<string> | null;
} & Pick<AssayGroupProps, 'assayRun' | 'assayRunInfo' | 'assayRunSchemaInfo' | 'cid'>;

export function AnalysisTemplateIntegrationRunAssayGroupBody({
  analysisTemplate,
  expandedState,
  setExpandedState,
  fileVariableBindings,
  setFileVariableBindings,
  analysisIds,
  ...props
}: AnalysisTemplateIntegrationRunAssayGroupBodyProps): JSX.Element {
  const analysesExist = analysisIds != null && analysisIds.length > 0;

  return (
    <>
      <div
        onClick={() => {
          setExpandedState({...expandedState, inputIsExpanded: !expandedState.inputIsExpanded});
        }}
        className={css(styles.sectionHeader)}
      >
        <span>Inputs</span>
        <BenchlingIcon
          icon={expandedState.inputIsExpanded ? 'chevron-down' : 'chevron-right'}
          className={css(styles.icon)}
        />
      </div>
      {expandedState.inputIsExpanded && (
        <>
          <AssayRunFields
            assayRun={props.assayRun}
            analysisTemplate={analysisTemplate}
            displayAnalysisTemplateIntegrationUI={true}
            liveFields={props.visibleRunFields}
            versionedFields={props.assayRunSchemaInfo.versionedFields}
            versionedTags={props.assayRunInfo.versionedTags}
            cid={props.cid}
            isEditable={!analysesExist}
            connectionSchemaId={null}
            versionedConnectionId={null}
            versionedEquipmentId={props.assayRunInfo.versionedEquipmentId}
            connectionStatus={null}
            hasAutomationFiles={false}
          />
          <InputFileFields
            analysisTemplate={analysisTemplate}
            runId={props.assayRun.apiId!}
            fileVariableBindings={fileVariableBindings ?? []}
            setFileVariableBindings={setFileVariableBindings}
          />
        </>
      )}
      <BenchlingQuery<GetAnalysesForAssayRun, GetAnalysesForAssayRunVariables>
        query={GET_ANALYSES_FOR_ASSAY_RUN_QUERY}
        variables={{
          analysisIds: analysisIds ?? [],
        }}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {(data, {refetch}) => {
          const accessibleAnalyses = data.analyses
            .filter((analysis) => !isInaccessible(analysis))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          return (
            accessibleAnalyses.length > 0 && ( // Don't show the output section at all if no analyses have been created from the run
              <>
                <hr />
                <div
                  onClick={() => {
                    setExpandedState({...expandedState, outputIsExpanded: !expandedState.outputIsExpanded});
                  }}
                  className={css(styles.sectionHeader)}
                >
                  <span>Outputs</span>
                  <BenchlingIcon
                    icon={expandedState.outputIsExpanded ? 'chevron-down' : 'chevron-right'}
                    className={css(styles.icon)}
                  />
                </div>
                {expandedState.outputIsExpanded && (
                  <div className={css(styles.outputsSectionWrapper)}>
                    {accessibleAnalyses.map((analysis) => (
                      <AnalysisChip key={analysis.id} id={analysis.id} size={ChipSize.SMALL} />
                    ))}
                  </div>
                )}
              </>
            )
          );
        }}
      </BenchlingQuery>
    </>
  );
}

const styles = StyleSheet.create({
  fileFieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  fileSelectorWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  icon: {
    width: 12,
    height: 12,
  },
  outputsSectionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
  },
  selectedFileText: {
    ...FONT_PROXIMA_13,
    color: GRAY_SEMIDARK,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
    cursor: 'pointer',
  },
  toolbar: {
    display: 'flex',
    gap: 8,
  },
});
